import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { JobindsatsElement } from '../core/jobindsats-element';
import { UpdateCalendarService } from './services/update-calendar-api-service';
import { TemaPageDto } from '@jobindsats/typescript-fetch-jobindsats';
import * as excelJs from '@jobindsats/exceljs';

@customElement('ji-update-calendar-excel-element')
export class UpdateCalendarExcelElement extends JobindsatsElement(LitElement) {
    static styles = [
        css`
            .excel-button-container {
                display: flex;
                justify-content: flex-end;
                padding-right: var(--column-spacing);
            }
            .excel-button {
                background: white;
                border: 2px solid var(--main-blue);
                padding: 15px 20px;
                font: inherit;
                font-weight: bold;
                font-size: 13px;
                cursor: pointer;
                color: var(--main-blue);
            }
            .excel-button:hover {
                background: var(--main-blue-10);
            }
        `
    ];

    @property({
        attribute: 'maaling-ids',
        type: Array
    })
    maalingIds?: string[];

    @property({
        attribute: 'maaling-urls'
    })
    maalingUrls?: string[];

    @property({
        attribute: 'maaling-names',
        type: Array
    })
    maalingNames?: string[];

    @property({
        attribute: 'maaling-groups'
    })
    maalingGroups?: string;

    @state()
    metadataMaalings?: TemaPageDto[] = [];

    @state()
    maalingExcelNames?: string[] = [];

    @state()
    maalingExcelGroupNames?: {Emne: string, Gruppe: string, MaalingId: string}[];

    maalingService: UpdateCalendarService;

    constructor() {
        super();
        this.maalingService = new UpdateCalendarService();
    }

    render() {
        return html`
                <div class="excel-button-container">
                    <button class="excel-button" @click=${this.exportButtonClicked}>
                        Eksportér til Excel
                    </button>
                </div>

        `;
    }

    exportButtonClicked() {
        var responseCount = 0;
        this.metadataMaalings = [];
        this.maalingExcelNames = [];
        
        if (this.maalingIds) {

            new Promise(() => {
                this.maalingIds?.forEach((maaling, index) => {
                    this.maalingService.getMetadataMaalingValues({ maalingId: maaling }).then(res => {
                        this.metadataMaalings?.push(res);
                        if (this.maalingNames) {
                            this.maalingExcelNames?.push(this.maalingNames[index]);
                        }
                        responseCount++;
                        if (responseCount === this.maalingIds?.length) {
                            this.createExcel();
                        }
                    }, reject => {
                        responseCount++;
                        if (responseCount === this.maalingIds?.length) {
                            this.createExcel();
                        }
                    });
                });
            });
        }
    }

    createExcel() {
        // Setup
        const workbook = new excelJs.Workbook();
        var ws = workbook.addWorksheet('Ark 1');
        var header = ['EMNE', 'GRUPPE', 'MÅLING', 'OPDATERET', 'NÆSTE OPDATERING', 'SENESTE PERIODE', 'OPDATERINGSFREKVENS'];
        var headerRow = ws.addRow(header);

        if (this.maalingGroups != null)
        {
            this.maalingExcelGroupNames = JSON.parse(this.maalingGroups);
        }

        // Sorting rows alphabetically using proxySheet
        var intermediateSheet: MaalingRow[] = [];
        this.metadataMaalings?.forEach((maaling, index) => {
            var lastPeriod = 'Ingen periode angivet';

            var excelGroup = this.maalingExcelGroupNames?.find(x => maaling.maalingId?.toLowerCase() === x.MaalingId.toLowerCase());

            if (maaling.period?.firstAndLast) {
                lastPeriod = maaling.period?.firstAndLast[1];
            }
            if (this.maalingExcelNames) {
                var row: MaalingRow = new MaalingRow(
                    excelGroup?.Emne ?? '',
                    excelGroup?.Gruppe ?? '',
                    this.maalingExcelNames[index],
                    maaling.updateInfo?.lastUpdate?.toLocaleDateString('da-DK', { year: "numeric", month: "short", day: "numeric" }).toString().trim() ?? '',
                    maaling.updateInfo?.nextUpdate?.toLocaleDateString('da-DK', { year: "numeric", month: "short", day: "numeric" }).toString().trim() ?? '',
                    lastPeriod.trim(),
                    maaling.updateFreq ?? ''
                );
                intermediateSheet.push(row);
            }
        });

        // Sort by Emne then Gruppe then Maaling
        intermediateSheet.sort(
            (a: MaalingRow, b: MaalingRow) =>
                100 * a.Emne.localeCompare(b.Emne, "da") + 10 * a.Gruppe.localeCompare(b.Gruppe, "da") + a.Maaling.localeCompare(b.Maaling, "da")
        );

        // Data
        intermediateSheet.forEach((row, index) => {
            var lastRow = ws.addRow(row.toArray());

            if (this.maalingExcelNames && index === this.maalingExcelNames?.length - 1) {
                lastRow.eachCell(function (cell) {
                    cell.border = {
                        bottom: { style: 'medium', color: { argb: 'FF003087' } },
                    }
                });
            }
        });

        ws.eachRow({ includeEmpty: true }, function (row) {
            row.eachCell({ includeEmpty: true }, function (cell) {
                cell.border = {
                    bottom: { style: 'hair', color: { argb: 'FF003087' } },
                    top: { style: 'hair', color: { argb: 'FF003087' } },
                    left: { style: 'hair', color: { argb: 'FF003087' } },
                    right: { style: 'hair', color: { argb: 'FF003087' } },
                }
            });
        });

        headerRow.eachCell({ includeEmpty: true }, function (cell) {
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFE5EAF3' },
            }
            cell.border = {
                bottom: { style: 'medium', color: { argb: 'FF003087' } },
                top: { style: 'medium', color: { argb: 'FF003087' } }
            }
        });


        // Adjusting column widths
        ws.columns.forEach((column) => {
            let maxColumnLength = 0;
            if (column.eachCell) {
                column.eachCell({ includeEmpty: true }, (cell) => {
                    maxColumnLength = Math.max(
                        maxColumnLength,
                        10,
                        cell.value ? cell.value.toString().length + 10 : 0
                    );
                });
            }
            column.width = maxColumnLength + 2;
        });

        // Saving the file
        var dateString = new Date().toLocaleDateString('da-DK').split('/').join('.');
        workbook.xlsx.writeBuffer()
            .then(xls64 => {
                var a = document.createElement('a');
                var data = new Blob([xls64], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                var url = URL.createObjectURL(data);
                a.href = url;
                a.download = 'Opdateringskalender.xlsx';
                document.body.appendChild(a);
                a.click();
                setTimeout(() => {
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);
                },
                    0);
            })
            .catch(error => {
                console.error(error.message);
            });
    }
}

class MaalingRow {
    Emne: string;
    Gruppe: string;
    Maaling: string;
    Opdateret: string;
    NaesteOpdatering: string;
    SenestePeriode: string;
    Opdateringsfrekvens: string;
    constructor(
        _Emne: string,
        _Gruppe: string,
        _Maaling: string,
        _Opdateret: string,
        _NaesteOpdatering: string,
        _SenestePeriode: string,
        _Opdateringsfrekvens: string,
    ) {
        this.Emne = _Emne;
        this.Gruppe = _Gruppe;
        this.Maaling = _Maaling;
        this.Opdateret = _Opdateret;
        this.NaesteOpdatering = _NaesteOpdatering;
        this.SenestePeriode = _SenestePeriode;
        this.Opdateringsfrekvens = _Opdateringsfrekvens;
    }

    toArray() {
        return [
            this.Emne,
            this.Gruppe,
            this.Maaling,
            this.Opdateret,
            this.NaesteOpdatering,
            this.SenestePeriode,
            this.Opdateringsfrekvens 
        ]
    }
}